// Schema-driven renderer for one policy group. Leaves show title / value / why; tables render from the
// schema's item properties; rows with nested objects (tiers, test cases) render as cards.
// NB: recursive renders use createElement — the visual-edits babel plugin loops on self-referencing JSX.
import { createElement } from "react";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ValueCell, isCondSchema } from "./policyShared";

const types = (s) => (Array.isArray(s?.type) ? s.type : [s?.type]);
const isObj = (s) => types(s).includes("object") && !!s.properties;
const isTable = (s) => types(s).includes("array") && !!s.items?.properties;
const hasNested = (s) => Object.values(s.items?.properties || {}).some((p) => isObj(p) || isTable(p));

export function emptyRow(itemsSchema) {
  const out = {};
  for (const [k, p] of Object.entries(itemsSchema?.properties || {})) {
    out[k] = isObj(p) ? emptyRow(p) : types(p).includes("array") ? [] : null;
  }
  return out;
}

function rowPaths(path, schema, row, i) {
  const key = schema["x-key"];
  const sel = key && row && row[key] != null ? `${path}[${key}=${row[key]}]` : `${path}[${i}]`;
  return { sel, idx: `${path}[${i}]` };
}

function LeafRow({ schema, value, selPath, idxPath, missing, editable, onEdit }) {
  const isMissing = missing?.has(idxPath);
  return (
    <div className={`grid grid-cols-1 md:grid-cols-[2fr_1.4fr_2fr_auto] gap-x-4 gap-y-1 px-3 py-2 border-t border-slate-100 ${isMissing ? "bg-amber-50/60" : ""}`} data-testid={`policy-leaf-${idxPath}`}>
      <div className="min-w-0">
        <div className="text-sm text-slate-800 flex items-center gap-1.5 flex-wrap">
          <span>{schema.title || selPath.split(".").pop()}</span>
          {schema["x-unit"] && <span className="text-[10px] text-slate-400">({schema["x-unit"]})</span>}
          {schema["x-clause"] && <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{schema["x-clause"]}</Badge>}
          {isMissing && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-300" data-testid="policy-required-badge">required before publish</Badge>}
        </div>
        <code className="text-[10px] text-slate-400 break-all">{selPath}</code>
      </div>
      <div className="min-w-0 break-words"><ValueCell value={value} unit={schema["x-unit"]} schema={schema} /></div>
      <div className="text-xs text-slate-500 min-w-0">{schema.description}</div>
      <div>
        {editable && (
          <button className="p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-700" title="Propose a change" onClick={() => onEdit({ mode: "edit", path: selPath, schema, value })} data-testid={`policy-edit-${idxPath}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TableView({ schema, rows, path, missing, editable, onEdit }) {
  const cols = Object.entries(schema.items.properties);
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs" data-testid={`policy-table-${path}`}>
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
          <tr>{cols.map(([k, p]) => <th key={k} className="text-left p-2 whitespace-nowrap">{p.title || k}</th>)}{editable && <th className="p-2" />}</tr>
        </thead>
        <tbody>
          {(rows || []).map((row, i) => {
            const { sel, idx } = rowPaths(path, schema, row, i);
            return (
              <tr key={sel} className="border-t border-slate-100 align-top" data-testid={`policy-row-${idx}`}>
                {cols.map(([k, p]) => (
                  <td key={k} className={`p-2 max-w-[260px] ${missing?.has(`${idx}.${k}`) ? "bg-amber-50/60" : ""}`}>
                    <ValueCell value={row?.[k]} unit={p["x-unit"]} schema={p} />
                    {editable && <button className="ml-1 align-middle text-slate-300 hover:text-emerald-700" title="Propose a change" onClick={() => onEdit({ mode: "edit", path: `${sel}.${k}`, schema: p, value: row?.[k] })} data-testid={`policy-edit-${idx}.${k}`}><Pencil className="w-3 h-3 inline" /></button>}
                  </td>
                ))}
                {editable && <td className="p-2"><button className="text-slate-300 hover:text-rose-600" title="Remove row" onClick={() => onEdit({ mode: "remove", path: sel, schema: schema.items, value: row })} data-testid={`policy-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button></td>}
              </tr>
            );
          })}
          {!(rows || []).length && <tr><td colSpan={cols.length + 1} className="p-3 text-center text-slate-400">No rows yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CardsView({ schema, rows, path, missing, editable, onEdit }) {
  const key = schema["x-key"];
  return (
    <div className="space-y-3 p-3">
      {(rows || []).map((row, i) => {
        const { sel, idx } = rowPaths(path, schema, row, i);
        return (
          <div key={sel} className="rounded-md border border-slate-200 bg-white" data-testid={`policy-card-${idx}`}>
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">{schema.items.title || schema.title} · <span className="font-mono">{key ? String(row?.[key] ?? i) : i}</span>{row?.name ? <span className="text-slate-500 font-normal"> — {row.name}</span> : null}</div>
              {editable && <button className="text-slate-400 hover:text-rose-600 text-xs flex items-center gap-1" onClick={() => onEdit({ mode: "remove", path: sel, schema: schema.items, value: row })} data-testid={`policy-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
            </div>
            {createElement(Node, { schema: schema.items, value: row, selPath: sel, idxPath: idx, missing, editable, onEdit, depth: 0 })}
          </div>
        );
      })}
      {!(rows || []).length && <div className="text-center text-sm text-slate-400 py-3">No rows yet</div>}
    </div>
  );
}

function Section({ title, description, path, depth, children, action }) {
  return (
    <div className={depth === 0 ? "" : "border-t border-slate-100"} data-testid={`policy-section-${path}`}>
      <div className={`flex items-center justify-between gap-2 px-3 py-2 ${depth === 0 ? "bg-slate-100" : "bg-slate-50/70"}`}>
        <div>
          <div className={`font-semibold text-slate-800 ${depth === 0 ? "text-sm" : "text-xs uppercase tracking-wide"}`}>{title}</div>
          {description && <div className="text-[11px] text-slate-500">{description}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Node({ schema, value, selPath, idxPath, missing, editable, onEdit, depth = 0 }) {
  if (!schema) return null;
  if (isTable(schema)) {
    const add = editable && (
      <button className="text-xs text-emerald-700 hover:underline flex items-center gap-1" onClick={() => onEdit({ mode: "add", path: `${selPath}[+]`, schema: schema.items, value: emptyRow(schema.items) })} data-testid={`policy-add-${idxPath}`}><Plus className="w-3.5 h-3.5" /> Add row</button>
    );
    const View = hasNested(schema) ? CardsView : TableView;
    return (
      <Section title={`${schema.title || selPath} (${(value || []).length})`} description={schema.description} path={idxPath} depth={depth} action={add}>
        <View schema={schema} rows={value} path={selPath} missing={missing} editable={editable} onEdit={onEdit} />
      </Section>
    );
  }
  if (isObj(schema) && !isCondSchema(schema)) {
    const entries = Object.entries(schema.properties);
    const leaves = entries.filter(([, p]) => !isObj(p) && !isTable(p));
    const nested = entries.filter(([, p]) => isObj(p) || isTable(p));
    const body = (
      <>
        {leaves.map(([k, p]) => <LeafRow key={k} schema={p} value={value?.[k]} selPath={`${selPath}.${k}`} idxPath={`${idxPath}.${k}`} missing={missing} editable={editable} onEdit={onEdit} />)}
        {nested.map(([k, p]) => createElement(Node, { key: k, schema: p, value: value?.[k], selPath: `${selPath}.${k}`, idxPath: `${idxPath}.${k}`, missing, editable, onEdit, depth: depth + 1 }))}
      </>
    );
    if (depth === 0) return body;
    return <Section title={schema.title || selPath.split(".").pop()} description={schema.description} path={idxPath} depth={depth}>{body}</Section>;
  }
  return <LeafRow schema={schema} value={value} selPath={selPath} idxPath={idxPath} missing={missing} editable={editable} onEdit={onEdit} />;
}

export default function PolicyDocView({ schema, doc, group, missing, editable = false, onEdit = () => {} }) {
  const gs = schema?.properties?.[group];
  if (!gs) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`policy-group-view-${group}`}>
      <div className="px-3 py-2.5 border-b border-slate-200">
        <div className="text-base font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{gs.title || group}</div>
        {gs.description && <div className="text-xs text-slate-500">{gs.description}</div>}
      </div>
      <Node schema={gs} value={doc?.[group]} selPath={group} idxPath={group} missing={missing} editable={editable} onEdit={onEdit} />
    </div>
  );
}
